import React, { useContext, useEffect, useState } from 'react';

import { observer } from 'mobx-react-lite';
import { useNavigate, useParams } from 'react-router-dom';

import { RootStoreContext } from 'index';
import { maskMonetaryValueAllowZero } from 'common/utils/string.utils';
import { getDateWithFormat } from 'common/dates/date.utils';
import { DateFormats } from 'common/dates/date.enums';
import { VectorIcon } from 'components/common/Icons/Icons';
import Spinner from 'components/common/Spinner/Spinner';
import { ApplicationRoute } from 'routes/routes.enums';
import Button from 'components/common/Button/Button';
import SideBar from 'components/common/SideBar/SideBar';
import IsNotMyCustomer from 'components/applications/ApplicationPage/IsNotMyCustomer/IsNotMyCustomer';
import { APPLICATIONS_STATUSES_MAPPING } from 'components/applications/applications.constants';

import styles from './ApplicationPage.module.scss';
import BorrowerField from './BorrowerField/BorrowerField';
import ContractUploadSection from './UploadContract/UploadContract';
import { ApplicationStatus } from '../applications.enums';

const ApplicationPage = () => {
  const { applicationsStore } = useContext(RootStoreContext);
  const { id } = useParams();
  const navigator = useNavigate();
  const { selectedApplication, isLoading } = applicationsStore;
  const [isOpenNotMyCustomerSidebar, setOpenNotMyCustomerSidebar] = useState(false);

  const handleClickOpenNotMyCustomerSidebar = () => {
    setOpenNotMyCustomerSidebar(true);
  };

  const handleCloseNotMyCustomerSidebar = () => {
    setOpenNotMyCustomerSidebar(false);
  };

  const getApplication = async () => {
    await applicationsStore.getApplicationById(id as string);
  };

  useEffect(() => {
    getApplication();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClickBack = () => {
    navigator(-1);
  };

  if (isLoading) {
    return <Spinner />;
  } else if (!selectedApplication) {
    navigator(ApplicationRoute.Other);

    return <></>;
  }

  const isShowUploadContractSection =
    selectedApplication.borrowerHomeImprovementEstimatesOrContractsRequired &&
    selectedApplication.status !== ApplicationStatus.Rejected;

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.applicationInfo}>
        <VectorIcon className={styles.backButton} onClick={handleClickBack} />
        <p className={styles.loanAmountText}>{maskMonetaryValueAllowZero(selectedApplication.loanAmount)}</p>
        <p className={styles.applicationStatus}>{APPLICATIONS_STATUSES_MAPPING[selectedApplication.status]}</p>
      </div>
      <div className={styles.applicationInfo}>
        <p className={styles.informationText}>ID #{selectedApplication.displayId}</p>
        <p className={styles.informationText}>
          Application Date:{' '}
          {getDateWithFormat(selectedApplication.applicationDate, DateFormats.FullEuDateWithMonthAbbreviation)}
        </p>
      </div>
      <div className={styles.applicationInfo}>
        <h3>
          {selectedApplication.borrowerFirstName} {selectedApplication.borrowerLastName}
        </h3>
        <Button className={styles.button} onClick={handleClickOpenNotMyCustomerSidebar} variant="underline">
          That Is Not My Customer
        </Button>
        <SideBar
          title="That is not my customer"
          isOpen={isOpenNotMyCustomerSidebar}
          onClose={handleCloseNotMyCustomerSidebar}
        >
          <IsNotMyCustomer onClose={handleCloseNotMyCustomerSidebar} applicationId={selectedApplication.id} />
        </SideBar>
      </div>
      <div className={styles.applicationsWrapper}>
        <div className={styles.borrowerWrapper}>
          <div className={styles.borrowerField}>
            <BorrowerField fieldTitle="Borrower First Name">{selectedApplication.borrowerFirstName}</BorrowerField>
            <BorrowerField fieldTitle="Borrower Last Name">{selectedApplication.borrowerLastName}</BorrowerField>
          </div>
          <div className={styles.borrowerField}>
            <BorrowerField fieldTitle="Borrower Phone">{selectedApplication.borrowerPhone}</BorrowerField>
            <BorrowerField fieldTitle="Borrower Email">{selectedApplication.borrowerEmail}</BorrowerField>
          </div>
          {selectedApplication.coborrowerFirstName && (
            <>
              <div className={styles.borrowerField}>
                <BorrowerField fieldTitle="Co-Borrower First Name">
                  {selectedApplication.coborrowerFirstName}
                </BorrowerField>
                <BorrowerField fieldTitle="Co-Borrower Last Name">
                  {selectedApplication.coborrowerLastName}
                </BorrowerField>
              </div>
              <div className={styles.borrowerField}>
                <BorrowerField fieldTitle="Co-Borrower Phone">{selectedApplication.coborrowerPhone}</BorrowerField>
                <BorrowerField fieldTitle="Co-Borrower Email">{selectedApplication.coborrowerEmail}</BorrowerField>
              </div>
            </>
          )}
          <BorrowerField fieldTitle="Borrower Address">
            {`${selectedApplication.borrowerHomeAddress.street_number || ''} ${selectedApplication.borrowerHomeAddress.street_name}`}
          </BorrowerField>
          <div className={styles.borrowerAddressWrapper}>
            <BorrowerField fieldTitle="City">{selectedApplication.borrowerHomeAddress.city}</BorrowerField>
            <BorrowerField fieldTitle="State">
              {selectedApplication.borrowerHomeAddress.state_or_province}
            </BorrowerField>
            <BorrowerField fieldTitle="Zip code">
              {selectedApplication.borrowerHomeAddress.zip_or_postal_code}
            </BorrowerField>
          </div>
        </div>
        {isShowUploadContractSection && <ContractUploadSection />}
      </div>
    </div>
  );
};

export default observer(ApplicationPage);
